import type {
  Chart,
  ChartData,
  ChartOptions,
  LegendItem,
  TooltipItem,
}
  from 'chart.js'
import type { IAdaptChart } from '~/types/shared-types'
import type { BaseChartTypes, PreparedChartParams, TChartDatasetWithDisplayValues } from './Chart.types'

const getDisplayValue = (dataset: { displayValues?: string[] } | undefined, index: number, fallback: unknown): string => {
  return dataset?.displayValues?.[index] ?? String(fallback)
}

const getChartTooltipText = (
  datasetLabel: string | undefined,
  value: string | number,
): string => {
  return `${datasetLabel || ''}: ${String(value)}`.trim()
}

export const defaultChartColors = [
  '#F54949',
  '#3CB507',
  '#FFBA26',
  '#9C5BEF',
  '#0967F7',
  '#00ACC7',
  '#FF69B4',
  '#8A2BE2',
  '#2CE107',
  '#EF9C7A',
]

export const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
    },
  },
}

export const defaultChartData = { labels: [0, 1], datasets: [0, 1] }

export const defaultLineChartData: ChartData<'line'> = {
  labels: defaultChartData.labels,
  datasets: [
    {
      label: '',
      data: defaultChartData.datasets,
      backgroundColor: 'rgba(37, 99, 235, 0.10)',
      borderColor: 'rgba(37, 99, 235, 0.25)',
      tension: 0.4,
      fill: false,
      borderWidth: 2,
    },
  ],
}

export const defaultLineChartOptions: ChartOptions<'line'> = {
  ...defaultChartOptions,
  plugins: {
    legend: {
      position: 'bottom' as const,
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'line'>) => {
          const value = context.parsed.y as number
          const dataset = context.dataset as TChartDatasetWithDisplayValues<'line'>
          const displayValue = getDisplayValue(dataset, context.dataIndex, value)

          return getChartTooltipText(context.dataset.label, displayValue)
        },
      },
    },
  },
  scales: {
    y: {
      grid: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}

export const defaultDoughnutChartData: ChartData<'doughnut'> = {
  labels: defaultChartData.labels,
  datasets: [
    {
      data: defaultChartData.datasets,
      borderWidth: 2,
      backgroundColor: defaultChartColors,
      borderColor: '#ffffff',
    },
  ],
}

const getChartTotal = (values: unknown[]): number => {
  let total = 0

  for (const item of values) {
    if (typeof item === 'number') {
      total += item
    }
  }

  return total
}

export const getChartLegendText = (
  label: unknown,
  value: string | number,
): string => {
  const displayValue = typeof value === 'string' ? value : String(value)
  return `${String(label)}: ${displayValue}`
}

export const getChartLegendTextWithPercentage = (
  label: unknown,
  value: string | number,
  total: number,
): string => {
  const numericValue = typeof value === 'number' ? value : Number(normalizeNumberishString(value))
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0
  const displayValue = typeof value === 'string' ? value : String(value)
  const percentage = total > 0 ? ((safeValue / total) * 100).toFixed(1) : '0.0'
  return `${String(label)}: ${displayValue} (${percentage}%)`
}

export const defaultDoughnutChartOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right' as const,
      align: 'center' as const,
      labels: {
        generateLabels: (chartInstance: Chart): LegendItem[] => {
          const dataset = chartInstance.data.datasets[0] as TChartDatasetWithDisplayValues<'doughnut'> | undefined
          const labels = chartInstance.data.labels ?? []
          const values = Array.isArray(dataset?.data) ? dataset.data : []
          const displayValues = dataset?.displayValues ?? []
          const colors = dataset?.backgroundColor
          const total = getChartTotal(values)

          return labels.map((label, index) => {
            const value = typeof values[index] === 'number' ? values[index] : 0
            const displayValue = displayValues[index] ?? value
            const color = Array.isArray(colors) ? colors[index] : colors

            return {
              text: getChartLegendTextWithPercentage(label, displayValue, total),
              fillStyle: color as string,
              strokeStyle: color as string,
              lineWidth: 0,
              hidden: !chartInstance.getDataVisibility(index),
              index,
            }
          })
        },
      },
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'doughnut'>) => {
          const total = getChartTotal(context.dataset.data as unknown[])
          const value = context.parsed as number
          const dataset = context.dataset as TChartDatasetWithDisplayValues<'doughnut'>
          const displayValue = dataset.displayValues?.[context.dataIndex] ?? value

          return ` ${getChartLegendTextWithPercentage(context.label, displayValue, total)}`
        },
      },
    },
  },
}

export const defaultBarChartData: ChartData<'bar'> = {
  labels: defaultChartData.labels,
  datasets: [
    {
      label: '',
      data: defaultChartData.datasets,
      borderWidth: 1,
      backgroundColor: defaultChartColors,
      borderColor: defaultChartColors,
    },
  ],
}

export const defaultBarChartOptions: ChartOptions<'bar'> = {
  ...defaultChartOptions,
  plugins: {
    ...defaultChartOptions.plugins,
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          const rawValue = context.parsed.x ?? context.parsed.y ?? 0
          const dataset = context.dataset as TChartDatasetWithDisplayValues<'bar'>
          const displayValue = getDisplayValue(dataset, context.dataIndex, rawValue)

          return ` ${getChartLegendText(context.dataset.label, displayValue)}`
        },
      },
    },
  },
  scales: {
    y: {
      stacked: true,
    },
    x: {
      stacked: true,
      grid: {
        display: false,
      },
    },
  },
  indexAxis: 'x',
}

export const defaultBarYChartOptions: ChartOptions<'bar'> = {
  ...defaultChartOptions,
  plugins: {
    ...defaultChartOptions.plugins,
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          const rawValue = context.parsed.x ?? context.parsed.y ?? 0
          const dataset = context.dataset as TChartDatasetWithDisplayValues<'bar'>
          const displayValue = getDisplayValue(dataset, context.dataIndex, rawValue)
          const total = getChartTotal(context.dataset.data as unknown[])
          return ` ${getChartLegendTextWithPercentage(context.label, displayValue, total)}`
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      reverse: true,
      grid: {
        display: false,
      },
    },
  },
  indexAxis: 'y',
}

const buildIndexedLabels = (labelsCount: number): string[] => {
  return Array.from({ length: labelsCount }, (_, index) => String(index + 1))
}

const resolveLabels = (chart: IAdaptChart, fallbackLabels: string[]): string[] => {
  return chart.labels.length ? chart.labels : fallbackLabels
}

const convertColorHexToRgba = (color: string | undefined, alpha: number, fallbackColor: string): string => {
  if (!color) {
    return fallbackColor
  }

  const normalizedColor = color.trim()
  const hexPattern = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

  if (!hexPattern.test(normalizedColor)) {
    return fallbackColor
  }

  const sanitizedHex = normalizedColor.slice(1)
  const fullHex = sanitizedHex.length === 3
    ? sanitizedHex.split('').map(char => `${char}${char}`).join('')
    : sanitizedHex

  const red = parseInt(fullHex.slice(0, 2), 16)
  const green = parseInt(fullHex.slice(2, 4), 16)
  const blue = parseInt(fullHex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const prepareBarChartData = (chart: IAdaptChart, hasMultiValues: boolean): ChartData<'bar'> => {
  if (hasMultiValues) {
    const maxValuesCount = Math.max(...chart.chartData.map(item => item.values.length))
    return {
      labels: resolveLabels(chart, buildIndexedLabels(maxValuesCount)),
      datasets: chart.chartData.map((item, index) => ({
        label: item.label || chart.title || `Series ${index + 1}`,
        data: item.values,
        displayValues: item.displayValues,
        borderWidth: 1,
        backgroundColor: item.color || defaultChartColors[index % defaultChartColors.length],
        borderColor: item.color || defaultChartColors[index % defaultChartColors.length],
      })),
    }
  }

  const data: ChartData<'bar'> = {
    ...defaultBarChartData,
    datasets: defaultBarChartData.datasets.map(dataset => ({ ...dataset })),
  }

  data.labels = resolveLabels(chart, chart.chartData.map((item) => item.label))
  if (data.datasets[0]) {
    const dataset = data.datasets[0] as TChartDatasetWithDisplayValues<'bar'>

    dataset.data = chart.chartData.map((item) => item.values[0] ?? 0)
    dataset.backgroundColor = chart.chartData.map((item) => item.color || defaultChartColors[0])
    dataset.borderColor = chart.chartData.map((item) => item.color || defaultChartColors[0])
    dataset.label = chart.title || ''
    dataset.displayValues = chart.chartData.map(
      item => item.displayValues?.[0] ?? String(item.values[0] ?? 0),
    )
  }

  return data
}

const prepareLineChartData = (chart: IAdaptChart, hasMultiValues: boolean): ChartData<'line'> => {
  if (hasMultiValues) {
    const maxValuesCount = Math.max(...chart.chartData.map(item => item.values.length))
    return {
      labels: resolveLabels(chart, buildIndexedLabels(maxValuesCount)),
      datasets: chart.chartData.map((item) => ({
        label: item.label || chart.title || '',
        data: item.values,
        displayValues: item.displayValues,
        borderColor: item.color || 'rgba(37, 99, 235, 0.25)',
        backgroundColor: convertColorHexToRgba(item.color, 0.1, 'rgba(37, 99, 235, 0.10)'),
        tension: 0.1,
        fill: true,
        borderWidth: 2,
      })),
    }
  }

  const data: ChartData<'line'> = {
    ...defaultLineChartData,
    datasets: defaultLineChartData.datasets.map(dataset => ({ ...dataset })),
  }

  data.labels = resolveLabels(chart, chart.chartData.map((item) => item.label))
  if (data.datasets[0]) {
    const dataset = data.datasets[0] as TChartDatasetWithDisplayValues<'line'>

    dataset.data = chart.chartData.map((item) => item.values[0] ?? 0)
    dataset.backgroundColor = chart.chartData.map(
      (item) => convertColorHexToRgba(item.color, 0.1, 'rgba(37, 99, 235, 0.10)'),
    )
    dataset.label = chart.title || ''
    dataset.displayValues = chart.chartData.map(
      item => item.displayValues?.[0] ?? String(item.values[0] ?? 0),
    )
  }

  return data
}

const prepareDoughnutChartData = (chart: IAdaptChart): ChartData<'doughnut'> => {
  const data: ChartData<'doughnut'> = {
    ...defaultDoughnutChartData,
    datasets: defaultDoughnutChartData.datasets.map(dataset => ({ ...dataset })),
  }

  data.labels = resolveLabels(chart, chart.chartData.map((item) => item.label))
  if (data.datasets[0]) {
    const dataset = data.datasets[0] as TChartDatasetWithDisplayValues<'doughnut'>

    dataset.data = chart.chartData.map((item) => item.values[0] ?? 0)
    dataset.backgroundColor = chart.chartData.map((item) => item.color || defaultChartColors[0])
    dataset.displayValues = chart.chartData.map(
      item => item.displayValues?.[0] ?? String(item.values[0] ?? 0),
    )
  }

  return data
}

const withPerPointLegend = (options: ChartOptions<'bar'>): ChartOptions<'bar'> => ({
  ...options,
  plugins: {
    ...options.plugins,
    legend: {
      ...options.plugins?.legend,
      labels: {
        ...options.plugins?.legend?.labels,
        padding: 16,
        boxWidth: 14,
        boxHeight: 14,
        generateLabels: (chartInstance: Chart): LegendItem[] => {
          const dataset = chartInstance.data.datasets[0] as TChartDatasetWithDisplayValues<'bar'> | undefined
          const colors = dataset?.backgroundColor
          const values = dataset?.data
          const total = Array.isArray(values) ? getChartTotal(values) : 0

          return (chartInstance.data.labels ?? []).map((label, index) => {
            const color = Array.isArray(colors) ? colors[index] : colors
            const value = Array.isArray(values) ? values[index] : undefined
            const displayValue = value === undefined ? undefined : getDisplayValue(dataset, index, value)

            return {
              text: displayValue === undefined
                ? String(label)
                : getChartLegendTextWithPercentage(label, displayValue, total),
              fillStyle: color as string,
              strokeStyle: color as string,
              lineWidth: 0,
              hidden: !chartInstance.getDataVisibility(index),
              index,
            }
          })
        },
      },
      onClick: (_event, legendItem: LegendItem, legend) => {
        if (typeof legendItem.index !== 'number') {
          return
        }

        legend.chart.toggleDataVisibility(legendItem.index)
        legend.chart.update()
      },
    },
  },
})

export const prepareChartParamsForType = (chart: IAdaptChart): PreparedChartParams => {
  const hasMultiValues = chart.chartData.some(item => item.values.length > 1)

  switch (chart.type) {
    case 'line':
      return {
        type: chart.type,
        data: prepareLineChartData(chart, hasMultiValues),
        options: defaultLineChartOptions as ChartOptions<BaseChartTypes>,
        title: chart.title,
      }
    case 'doughnut':
      return {
        type: chart.type,
        data: prepareDoughnutChartData(chart),
        options: defaultDoughnutChartOptions as ChartOptions<BaseChartTypes>,
        title: chart.title,
      }
    case 'bar':
      return {
        type: chart.type,
        data: prepareBarChartData(chart, hasMultiValues),
        options: (hasMultiValues
          ? defaultBarChartOptions
          : withPerPointLegend(defaultBarChartOptions)) as ChartOptions<BaseChartTypes>,
        title: chart.title,
      }
    case 'barY':
      return {
        type: chart.type,
        data: prepareBarChartData(chart, hasMultiValues),
        options: (hasMultiValues
          ? defaultBarYChartOptions
          : withPerPointLegend(defaultBarYChartOptions)) as ChartOptions<BaseChartTypes>,
        title: chart.title,
      }
  }
}

/*
Normalizes number strings with various separators to a standard format.
- Removes spaces and apostrophes
- Treats the rightmost separator as decimal, all others as thousands separators
Examples: "1,234.56" → "1234.56", "1.234,56" → "1234.56", "1,234,567" → "1234567"
*/
export function normalizeNumberishString(rawValue: string): string {
  // Remove spaces and apostrophes
  let value = rawValue.trim().replace(/[\s''`]/g, '')

  const lastCommaIdx = value.lastIndexOf(',')
  const lastDotIdx = value.lastIndexOf('.')

  // No separators found
  if (lastCommaIdx === -1 && lastDotIdx === -1) {
    return value
  }

  // The rightmost separator is the decimal separator
  if (lastCommaIdx > lastDotIdx) {
    // Comma is decimal, dot is thousands separator
    value = value.replace(/\./g, '')
    value = value.replace(/,(?=[^,]*$)/, '.')
  } else {
    // Dot is decimal, comma is thousands separator
    value = value.replace(/,/g, '')
  }

  return value
}
